import { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api, {
  hasDevToken,
  CtiDeploymentRow,
  CtiDeploymentStats,
  CtiDeploymentListResponse,
  CtiDeployEvent,
} from '../services/api';
import { labelForAwxJobStatus } from '../services/status-labels';

// Same AWX terminal-status palette as AwxJobsPage so an operator sees a
// consistent colour language across both job surfaces.
const STATUS_COLORS: Record<string, string> = {
  successful: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-700',
  error: 'bg-red-100 text-red-700',
  canceled: 'bg-slate-200 text-slate-700',
  pending: 'bg-amber-100 text-amber-800',
  waiting: 'bg-amber-100 text-amber-800',
  running: 'bg-blue-100 text-blue-800',
  new: 'bg-blue-100 text-blue-800',
};

type StatusFilter = 'all' | 'pending' | 'finished';

const POLL_INTERVAL_MS = 5_000;
const SSE_RECONNECT_HINT_AFTER = 3;

export function CtiDeploymentsPage() {
  const [rows, setRows] = useState<CtiDeploymentRow[]>([]);
  const [stats, setStats] = useState<CtiDeploymentStats>({ total: 0, pending: 0, failed: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [selected, setSelected] = useState<CtiDeploymentRow | null>(null);
  const [streamState, setStreamState] = useState<'connecting' | 'live' | 'polling'>('connecting');
  const reconnectCountRef = useRef(0);

  const refresh = useCallback((opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    return api.get<CtiDeploymentListResponse>('/cti-deployments', { params: { status: filter, limit: 200 } })
      .then((r) => {
        setRows(r.data.rows);
        setStats(r.data.stats);
      })
      .finally(() => { if (!opts?.silent) setLoading(false); });
  }, [filter]);

  useEffect(() => { refresh(); }, [refresh]);

  // Live updates: prefer SSE, fall back to polling (dev bearer / SSE-stripping
  // proxies). Identical wiring to AwxJobsPage.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.EventSource === 'undefined') {
      setStreamState('polling');
      const id = window.setInterval(() => { refresh({ silent: true }); }, POLL_INTERVAL_MS);
      return () => window.clearInterval(id);
    }
    if (hasDevToken()) {
      setStreamState('polling');
      const id = window.setInterval(() => { refresh({ silent: true }); }, POLL_INTERVAL_MS);
      return () => window.clearInterval(id);
    }

    const es = new EventSource('/api/cti-deployments/stream');
    es.addEventListener('hello', () => {
      setStreamState('live');
      reconnectCountRef.current = 0;
    });
    es.addEventListener('cti.deploy', (ev) => {
      try {
        const data = JSON.parse((ev as MessageEvent<string>).data) as CtiDeployEvent;
        setRows((prev) => {
          const idx = prev.findIndex((r) => r.id === data.id);
          if (idx === -1) {
            refresh({ silent: true });
            return prev;
          }
          const merged: CtiDeploymentRow = {
            ...prev[idx],
            target: data.target,
            host: data.host,
            vmTemplate: data.vmTemplate,
            profiles: data.profiles,
            status: data.status,
            startedAt: data.startedAt,
            finishedAt: data.finishedAt,
          };
          const next = prev.slice();
          next[idx] = merged;
          return next;
        });
        setStats((prev) => deriveStats(prev, data.status));
      } catch {
        refresh({ silent: true });
      }
    });
    es.onerror = () => {
      reconnectCountRef.current += 1;
      if (reconnectCountRef.current >= SSE_RECONNECT_HINT_AFTER) setStreamState('polling');
    };
    return () => { es.close(); };
  }, [refresh]);

  useEffect(() => {
    if (streamState !== 'polling') return;
    const id = window.setInterval(() => { refresh({ silent: true }); }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [streamState, refresh]);

  return (
    <div className="relative">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold">CTI-Deployments</h2>
          <p className="text-sm text-slate-500">
            Provisioniert eine nested-KVM-VM und deployt den SecTepe.Core.CTI
            Docker/CAPE/Sandbox-Stack. Status live via SSE; Fallback alle 5 s.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <StreamBadge state={streamState} />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as StatusFilter)}
            className="px-2 py-1 rounded border"
          >
            <option value="all">Alle</option>
            <option value="pending">Laufend</option>
            <option value="finished">Beendet</option>
          </select>
          <Link to="/cti-deployments/new" className="btn-primary">Deployen</Link>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard label="Total" value={stats.total} tone="slate" />
        <StatCard label="Laufend" value={stats.pending} tone="amber" />
        <StatCard label="Failed" value={stats.failed} tone="red" />
      </div>

      {loading ? <p className="text-slate-500">Lade…</p> : (
        <table className="w-full bg-white shadow rounded text-sm">
          <thead className="bg-slate-100 text-left uppercase tracking-wide">
            <tr>
              <th className="p-3">Ziel</th>
              <th className="p-3">VM-Template</th>
              <th className="p-3">Profile</th>
              <th className="p-3">Status</th>
              <th className="p-3">Gestartet</th>
              <th className="p-3">Beendet</th>
              <th className="p-3">AWX-Job-ID</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr
                key={d.id}
                onClick={() => setSelected(d)}
                className={`border-t cursor-pointer hover:bg-slate-50 ${selected?.id === d.id ? 'bg-slate-50' : ''}`}
              >
                <td className="p-3">
                  {d.tenantSlug ? (
                    <Link
                      to={`/tenants/${d.tenantSlug}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-primary-600 hover:underline font-mono"
                    >
                      {d.tenantSlug}
                    </Link>
                  ) : (
                    <span className="font-mono text-slate-700">{d.target}</span>
                  )}
                </td>
                <td className="p-3 font-mono text-xs">{d.vmTemplate}</td>
                <td className="p-3 text-xs">{d.profiles.join(', ')}</td>
                <td className="p-3">
                  <span className={`badge ${STATUS_COLORS[d.status] ?? 'bg-slate-200 text-slate-700'}`}>
                    {labelForAwxJobStatus(d.status)}
                  </span>
                </td>
                <td className="p-3 text-slate-500">{fmtDate(d.startedAt)}</td>
                <td className="p-3 text-slate-500">{fmtDate(d.finishedAt)}</td>
                <td className="p-3 font-mono">{d.id}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-slate-500">
                Keine CTI-Deployments für diesen Filter.
              </td></tr>
            )}
          </tbody>
        </table>
      )}

      {selected && <DeploymentDetailPanel deployment={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

interface StatCardProps { label: string; value: number; tone: 'slate' | 'amber' | 'red'; }
function StatCard({ label, value, tone }: StatCardProps) {
  const toneClass = { slate: 'text-slate-800', amber: 'text-amber-700', red: 'text-red-700' }[tone];
  return (
    <div className="bg-white rounded shadow p-4">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`text-3xl font-semibold ${toneClass}`}>{value}</p>
    </div>
  );
}

function StreamBadge({ state }: { state: 'connecting' | 'live' | 'polling' }) {
  const map = {
    connecting: { label: 'connecting…', cls: 'bg-slate-100 text-slate-600' },
    live: { label: '● live', cls: 'bg-green-100 text-green-800' },
    polling: { label: 'polling (5s)', cls: 'bg-amber-100 text-amber-800' },
  } as const;
  return <span className={`badge ${map[state].cls}`}>{map[state].label}</span>;
}

interface DeploymentDetailPanelProps { deployment: CtiDeploymentRow; onClose: () => void; }
function DeploymentDetailPanel({ deployment, onClose }: DeploymentDetailPanelProps) {
  return (
    <div className="fixed top-0 right-0 w-full max-w-xl h-full bg-white shadow-2xl border-l border-slate-200 overflow-y-auto z-50">
      <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
        <h3 className="font-semibold">CTI-Deployment {deployment.id}</h3>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-800 text-2xl leading-none px-2">×</button>
      </div>
      <div className="p-5 space-y-4 text-sm">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Ziel"><code className="font-mono text-xs">{deployment.target}</code></Field>
          <Field label="Status">
            <span className={`badge ${STATUS_COLORS[deployment.status] ?? 'bg-slate-200 text-slate-700'}`}>
              {labelForAwxJobStatus(deployment.status)}
            </span>
          </Field>
          <Field label="VM-Template"><code className="font-mono text-xs">{deployment.vmTemplate}</code></Field>
          <Field label="Host"><code className="font-mono text-xs">{deployment.host ?? '—'}</code></Field>
          <Field label="Profile">{deployment.profiles.join(', ') || '—'}</Field>
          <Field label="AWX-Job-ID"><code className="font-mono">{deployment.id}</code></Field>
          <Field label="Gestartet">{fmtDate(deployment.startedAt)}</Field>
          <Field label="Beendet">{fmtDate(deployment.finishedAt)}</Field>
          <Field label="Angelegt von"><code className="font-mono text-xs">{deployment.createdBy}</code></Field>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Payload</p>
          <pre className="bg-slate-50 border border-slate-200 rounded p-3 text-xs overflow-x-auto">
            {JSON.stringify(deployment.payload, null, 2)}
          </pre>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Result</p>
          <pre className="bg-slate-50 border border-slate-200 rounded p-3 text-xs overflow-x-auto">
            {deployment.result === null || deployment.result === undefined ? '—' : JSON.stringify(deployment.result, null, 2)}
          </pre>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

// Same conservative incremental stats update as AwxJobsPage.deriveStats.
function deriveStats(prev: CtiDeploymentStats, newStatus: string): CtiDeploymentStats {
  const isTerminal = newStatus === 'successful' || newStatus === 'failed';
  return {
    total: prev.total,
    pending: isTerminal ? Math.max(0, prev.pending - 1) : prev.pending,
    failed: newStatus === 'failed' ? prev.failed + 1 : prev.failed,
  };
}
